import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Alert, AlertTitle } from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Swal from 'sweetalert2';
import * as XLSX from 'exceljs';
import SCUIGenericExcelUpload from '../../../atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import SCUIDownloadButton from '../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import MobilePanelClientTranslationsCommand from '../../../services/command/mobilepanelclienttranslation/MobilePanelClientTranslationsCommand.services';
import { MPCT } from '../mpClientTranslations.classes';

export const BulkUploadTab = (): React.JSX.Element => {
    const { t } = useTranslation();
    const [uploadResponse, setUploadResponse] = useState<any>(null);
    const [bulkUploadResult, setBulkUploadResult] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const exampleData = useMemo(
        () => [
            {
                CountryId: 1,
                CultureCode: 'tr-TR',
                Key: 'welcomeMessage',
                Value: 'Uygulamaya hoş geldiniz',
            },
            {
                CountryId: 1,
                CultureCode: 'en-US',
                Key: 'welcomeMessage',
                Value: 'Welcome to our application',
            },
            {
                CountryId: 2,
                CultureCode: 'de-DE',
                Key: 'loginButton',
                Value: 'Anmelden',
            },
        ],
        []
    );

    // Grid columns for bulk upload result
    const bulkUploadColumns: ExtendedGridColDef[] = useMemo(
        () => [
            {
                field: 'operationType',
                headerName: t('general.operation'),
                width: 120,
                renderCell: (params: any) => {
                    const type = params.value;
                    if (type === 'inserted') {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4caf50' }}>
                                <AddCircleOutlineIcon fontSize="small" />
                                <span>{t('general.inserted')}</span>
                            </Box>
                        );
                    } else if (type === 'updated') {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ff9800' }}>
                                <EditIcon fontSize="small" />
                                <span>{t('general.updated')}</span>
                            </Box>
                        );
                    } else if (type === 'failed') {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f44336' }}>
                                <ErrorOutlineIcon fontSize="small" />
                                <span>{t('general.failed')}</span>
                            </Box>
                        );
                    }
                    return '-';
                },
            },
            {
                field: 'countryId',
                headerName: t('mobilePanel.countryId'),
                width: 100,
            },
            {
                field: 'cultureCode',
                headerName: t('mobilePanel.cultureCode'),
                width: 120,
            },
            {
                field: 'key',
                headerName: t('mobilePanel.translationKey'),
                flex: 1,
                minWidth: 200,
            },
            {
                field: 'value',
                headerName: t('mobilePanel.translationValue'),
                flex: 1,
                minWidth: 200,
            },
            {
                field: 'errorMessage',
                headerName: t('general.message'),
                flex: 1,
                minWidth: 180,
                renderCell: (params: any) =>
                    params.value ? <span style={{ color: 'red' }}>{params.value}</span> : '-',
            },
        ],
        [t]
    );

    // Helper function to clean values (remove quotes and trim whitespace)
    const cleanValue = (value: any): string => {
        if (!value) return '';
        let str = String(value).trim();
        // Remove leading and trailing quotes (" or ')
        str = str.replace(/^["']|["']$/g, '');
        return str.trim();
    };

    const onUpload = async (file: File) => {
        setIsLoading(true);
        setUploadResponse(null);
        setBulkUploadResult([]);

        try {
            // Read the Excel file using exceljs
            const workbook = new XLSX.Workbook();
            const arrayBuffer = await file.arrayBuffer();
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.worksheets[0];
            if (!worksheet) {
                throw new Error('No worksheet found');
            }

            // Parse rows to JSON using column order (more reliable than header names)
            const jsonData: any[] = [];
            let hasHeaders = false;

            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) {
                    // Check if first row looks like headers (skip it)
                    const firstCellValue = cleanValue(row.getCell(1).value);
                    hasHeaders = isNaN(Number(firstCellValue)); // If first cell is not a number, it's likely a header
                    console.log('First row detected as:', hasHeaders ? 'Headers' : 'Data');
                    if (hasHeaders) return; // Skip header row
                }

                // Read columns by position (0-based after adjustment)
                const countryIdCell = cleanValue(row.getCell(1).value);
                const cultureCodeCell = cleanValue(row.getCell(2).value);
                const keyCell = cleanValue(row.getCell(3).value);
                const valueCell = cleanValue(row.getCell(4).value);

                // Validate that we have all required fields
                if (countryIdCell && cultureCodeCell && keyCell && valueCell) {
                    const rowData = {
                        countryId: countryIdCell,
                        cultureCode: cultureCodeCell,
                        key: keyCell,
                        value: valueCell,
                    };

                    // Log first data row for debugging
                    if (jsonData.length === 0) {
                        console.log('First data row:', rowData);
                    }

                    jsonData.push(rowData);
                } else if (countryIdCell || cultureCodeCell || keyCell || valueCell) {
                    // Log rows with some data but missing required fields
                    console.warn(`Row ${rowNumber} missing required fields:`, {
                        countryId: countryIdCell || '❌ missing',
                        cultureCode: cultureCodeCell || '❌ missing',
                        key: keyCell || '❌ missing',
                        value: valueCell || '❌ missing',
                    });
                }
            });

            console.log('Parsed JSON Data:', jsonData.length, 'rows');

            // Map Excel data to API format
            const translations = jsonData.map((row: any) => {
                // Safe number conversion
                let countryId = 0;
                if (row?.countryId) {
                    const parsed = parseInt(String(row.countryId), 10);
                    countryId = isNaN(parsed) ? 0 : parsed;
                }

                return {
                    countryId: countryId,
                    cultureCode: cleanValue(row?.cultureCode),
                    key: cleanValue(row?.key),
                    value: cleanValue(row?.value),
                };
            });

            console.log('Translations to send:', translations.slice(0, 3)); // Log first 3 for debugging

            if (translations.length === 0) {
                throw new Error(t('mobilePanel.noValidDataFound'));
            }

            const userName = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username ?? '';

            const requestPayload = {
                userName: userName,
                translations,
            };

            const response =
                await MobilePanelClientTranslationsCommand.BulkUploadMobilePanelClientTranslations(requestPayload);

            if (response) {
                // Response structure: { model: BulkUploadResultModel }
                const resultModel = response?.model || response;
                console.log('Upload Response:', resultModel);
                console.log('Status Count Info:', resultModel?.statusCountInfo);

                // Process response items for the grid
                const responseItems = resultModel?.items ?? [];
                const formattedResult =
                    Array.isArray(responseItems) && responseItems.length > 0
                        ? responseItems.map((item: any, idx: number) => ({
                              id: idx + 1,
                              countryId: translations[idx]?.countryId ?? 0,
                              cultureCode: translations[idx]?.cultureCode ?? '',
                              key: item?.key ?? '',
                              value: item?.value ?? '',
                              isSuccess: item?.isSuccess ?? false,
                              errorMessage: item?.errorMessage ?? '',
                              operationType: item?.operationType ?? 'unknown',
                          }))
                        : [];

                console.log('Prepared data - uploadResponse:', resultModel);
                console.log('Prepared data - bulkUploadResult:', formattedResult);

                // Stop loading FIRST to prevent component remount
                setIsLoading(false);

                // Then update state after a small delay to ensure component stability
                setTimeout(() => {
                    setUploadResponse(resultModel);
                    setBulkUploadResult(formattedResult);

                    console.log('State set - uploadResponse:', resultModel);
                    console.log('State set - bulkUploadResult:', formattedResult);

                    // Show success message
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        text: t('mobilePanel.bulkUploadSuccessMessage'),
                        confirmButtonText: t('general.ok'),
                        showConfirmButton: true,
                    });
                }, 100);

                // Note: We don't refresh the grid here to preserve upload results
                // The grid will be refreshed when user switches back to the List tab
            }
        } catch (error: any) {
            console.error('Excel upload error:', error);
            setIsLoading(false);
            setUploadResponse(null);
            setBulkUploadResult([]);
            let errorMessage = t('mobilePanel.bulkUploadError');

            if (error?.data?.Key) {
                errorMessage = error.data.Key;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: errorMessage,
                confirmButtonText: t('general.ok'),
            });
        }
    };

    // Render status counts
    const renderStatusCounts = () => {
        console.log('Render Status Counts - uploadResponse:', uploadResponse);

        // Try both camelCase and PascalCase
        const statusInfo = uploadResponse?.statusCountInfo || uploadResponse?.StatusCountInfo;

        if (!statusInfo) {
            console.warn(
                'No statusCountInfo found in uploadResponse. Available keys:',
                Object.keys(uploadResponse || {})
            );
            return null;
        }

        const {
            totalRecords = 0,
            updatedRecords = 0,
            insertedRecords = 0,
            failedRecords = 0,
            TotalRecords = 0,
            UpdatedRecords = 0,
            InsertedRecords = 0,
            FailedRecords = 0,
        } = statusInfo;

        const total = totalRecords || TotalRecords;
        const updated = updatedRecords || UpdatedRecords;
        const inserted = insertedRecords || InsertedRecords;
        const failed = failedRecords || FailedRecords;

        console.log('Status Counts:', { total, updated, inserted, failed });

        return (
            <Box className={MPCT.bulkTabStatusRow} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, marginBottom: 2 }}>
                <Chip
                    label={`${t('mobilePanel.total')}: ${total}`}
                    sx={{ backgroundColor: '#2196f3', color: 'white' }}
                />
                <Chip
                    label={`${t('mobilePanel.updated')}: ${updated}`}
                    sx={{ backgroundColor: '#ff9800', color: 'white' }}
                />
                <Chip
                    label={`${t('mobilePanel.inserted')}: ${inserted}`}
                    sx={{ backgroundColor: '#4caf50', color: 'white' }}
                />
                <Chip
                    label={`${t('mobilePanel.failed')}: ${failed}`}
                    sx={{ backgroundColor: '#f44336', color: 'white' }}
                />
            </Box>
        );
    };

    console.log('BulkUploadTab RENDER - uploadResponse:', uploadResponse);
    console.log('BulkUploadTab RENDER - bulkUploadResult:', bulkUploadResult.length, 'items');

    return (
        <Box className={MPCT.bulkTabRoot} width="100%">
            <p className={MPCT.bulkTabTitle} style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {t('mobilePanel.bulkUpload')}
            </p>
            <p className={MPCT.bulkTabSubtitle} style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('mobilePanel.bulkUploadText')}
            </p>

            {/* Warning Alert */}
            <Alert className={MPCT.bulkTabWarningAlert} severity="warning" sx={{ marginTop: '16px', marginBottom: '16px' }}>
                <AlertTitle>{t('general.warning')}</AlertTitle>
                <Box component="div">
                    <strong>{t('mobilePanel.bulkUploadWarningTitle')}</strong>
                    <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                        <li>{t('mobilePanel.bulkUploadWarning1')}</li>
                        <li>{t('mobilePanel.bulkUploadWarning2')}</li>
                        <li>{t('mobilePanel.bulkUploadWarning3')}</li>
                    </ul>
                </Box>
            </Alert>

            <Box className={MPCT.bulkTabUploadWrap}>
                <SCUIGenericExcelUpload
                    onUpload={onUpload}
                    uploadButtonText={t('mobilePanel.upload')}
                    selectFileButtonText={t('mobilePanel.selectExcelFile')}
                    showExample={true}
                    exampleData={exampleData}
                    exampleButtonText={t('general.exampleExcel')}
                    loading={isLoading}
                    style={{ width: '100%' }}
                    exampleFileName={t('mobilePanel.exampleFileName')}
                />
            </Box>

            {/* Upload Results Summary - Always visible */}
            <Box className={MPCT.bulkTabSummarySection} sx={{ marginTop: 3, marginBottom: 2 }}>
                {uploadResponse ? (
                    <Box
                        className={MPCT.bulkTabSummaryActive}
                        sx={{
                            borderLeft: '4px solid #2196f3',
                            paddingLeft: 2,
                            marginBottom: 2,
                            backgroundColor: '#f5f5f5',
                            padding: 2,
                            borderRadius: 1,
                        }}
                    >
                        <p className={MPCT.bulkTabSummaryTitle} style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>
                            {t('mobilePanel.uploadSummary')}
                        </p>
                        {renderStatusCounts()}
                    </Box>
                ) : (
                    <Box className={MPCT.bulkTabSummaryPlaceholder} sx={{ padding: 2, backgroundColor: '#f9f9f9', borderRadius: 1, border: '1px dashed #ccc' }}>
                        <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
                            ℹ️ {t('mobilePanel.noUploadYet')}
                        </p>
                    </Box>
                )}
            </Box>

            {/* Display bulk upload result grid */}
            {bulkUploadResult.length > 0 && (
                <Box className={MPCT.bulkTabResultsSection} sx={{ mt: 4 }}>
                    <Box className={MPCT.bulkTabResultsToolbar} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <p className={MPCT.bulkTabResultsTitle} style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            {t('mobilePanel.uploadResults')}
                        </p>
                        <Box className={MPCT.bulkTabResultsDownloadWrap}>
                            <SCUIDownloadButton
                                position="right"
                                excelData={{
                                    excelDownloadData: bulkUploadResult,
                                    excelDownloadFolderName: t('mobilePanel.uploadResultsFileName'),
                                    excelDownloadButtonText: t('mobilePanel.exportUploadResults'),
                                    hiddenExcelColumn: ['id', 'isSuccess'],
                                }}
                            />
                        </Box>
                    </Box>
                    <Box className={MPCT.bulkTabResultsGridWrap}>
                        <SCUICustomDataGrid
                            rows={bulkUploadResult}
                            columns={bulkUploadColumns}
                            autoHeight
                            getRowId={(row: any) => row?.id}
                            getRowClassName={(params: any) => {
                                const type = params.row.operationType;
                                if (type === 'inserted') return 'inserted-row';
                                if (type === 'updated') return 'updated-row';
                                if (type === 'failed') return 'failed-row';
                                return '';
                            }}
                            sx={{
                                '& .inserted-row': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(76, 175, 80, 0.15)',
                                    },
                                },
                                '& .updated-row': {
                                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 152, 0, 0.15)',
                                    },
                                },
                                '& .failed-row': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default BulkUploadTab;
